import {
  buildStepToWorkflowMap,
  EventType,
  FailureContext,
  generateEventTypeMapDefaults,
  StepToWorkflowMap,
  WorkflowEventEventTypeMap,
  WorkflowEventMetaDataMapWithDefaults,
} from '@anduin/shared';

export enum PaymentPlansWorkflowType {
  PLANS = 'PAYMENT_PLANS',
}

export enum PaymentsPlansStep {
  VIEWED = 'PAYMENT_PLANS_VIEWED',
  SEARCHED = 'PAYMENT_PLANS_SEARCHED',
  COLUMN_FILTER_CHANGED = 'PAYMENT_PLANS_COLUMN_FILTER_CHANGED',
  SORT_CHANGED = 'PAYMENT_PLANS_SORT_CHANGED',
  EXPORT_CLICKED = 'PAYMENT_PLANS_EXPORT_CLICKED',
  MORE_MENU_OPENED = 'PAYMENT_PLANS_MORE_MENU_OPENED',
  EDIT_IN_CLIENT_PORTAL_SUBMITTED = 'PAYMENT_PLANS_EDIT_IN_CLIENT_PORTAL_SUBMITTED',
  EDIT_IN_CLIENT_PORTAL_FAILED = 'PAYMENT_PLANS_EDIT_IN_CLIENT_PORTAL_FAILED',
  EMPTY_STATE_SHOWN = 'PAYMENT_PLANS_EMPTY_STATE_SHOWN',
}

export type PaymentPlansSurface = 'STANDALONE' | 'CLIENT_DETAIL';
export type PaymentPlansVersionLabel = 'v3' | 'v4';
export type PaymentPlansEmptyStateReason = 'NO_PLANS' | 'FILTERED_OUT' | 'LOAD_ERROR';

/**
 * Context fields stamped onto every Payment Plans event by `PaymentPlansTrackService`.
 *
 * `planCountVisible` is nullable because the AG-Grid api is only available after
 * `onGridReady`; `VIEWED` emitted from `ngOnInit` stamps `null` until the grid is mounted.
 */
export interface PaymentPlansBaseMetadata {
  planCountVisible: number | null;
  surface: PaymentPlansSurface;
  planVersion: PaymentPlansVersionLabel;
  activeFilters: string;
}

export interface PaymentPlansSearchedMetadata extends PaymentPlansBaseMetadata {
  queryLength: number;
}

export interface PaymentPlansColumnFilterChangedMetadata extends PaymentPlansBaseMetadata {
  columnId: string;
}

export interface PaymentPlansSortChangedMetadata extends PaymentPlansBaseMetadata {
  columnId: string;
  direction: 'asc' | 'desc' | null;
}

export interface PaymentPlansExportClickedMetadata extends PaymentPlansBaseMetadata {
  rowCountVisible: number;
}

export interface PaymentPlansPlanIdMetadata extends PaymentPlansBaseMetadata {
  planId: number;
}

export interface PaymentPlansEditInClientPortalSubmittedMetadata extends PaymentPlansBaseMetadata {
  planId: number;
  clientId: number;
}

export interface PaymentPlansEditInClientPortalFailedMetadata
  extends PaymentPlansEditInClientPortalSubmittedMetadata,
    FailureContext {
  operationDurationMs: number;
}

export interface PaymentPlansEmptyStateShownMetadata extends PaymentPlansBaseMetadata {
  reason: PaymentPlansEmptyStateReason;
}

interface PaymentPlansStepMetaDataOverrides {
  [PaymentsPlansStep.VIEWED]: PaymentPlansBaseMetadata;
  [PaymentsPlansStep.SEARCHED]: PaymentPlansSearchedMetadata;
  [PaymentsPlansStep.COLUMN_FILTER_CHANGED]: PaymentPlansColumnFilterChangedMetadata;
  [PaymentsPlansStep.SORT_CHANGED]: PaymentPlansSortChangedMetadata;
  [PaymentsPlansStep.EXPORT_CLICKED]: PaymentPlansExportClickedMetadata;
  [PaymentsPlansStep.MORE_MENU_OPENED]: PaymentPlansPlanIdMetadata;
  [PaymentsPlansStep.EDIT_IN_CLIENT_PORTAL_SUBMITTED]: PaymentPlansEditInClientPortalSubmittedMetadata;
  [PaymentsPlansStep.EDIT_IN_CLIENT_PORTAL_FAILED]: PaymentPlansEditInClientPortalFailedMetadata;
  [PaymentsPlansStep.EMPTY_STATE_SHOWN]: PaymentPlansEmptyStateShownMetadata;
}

export type PaymentPlansWorkflowStepMetaDataMap = WorkflowEventMetaDataMapWithDefaults<
  PaymentsPlansStep,
  PaymentPlansStepMetaDataOverrides
>;

export type PaymentPlansStepMetadataInput<E extends PaymentsPlansStep> = Omit<
  PaymentPlansWorkflowStepMetaDataMap[E],
  keyof PaymentPlansBaseMetadata
>;

const PAYMENT_PLANS_WORKFLOW_STEPS = {
  [PaymentPlansWorkflowType.PLANS]: PaymentsPlansStep,
} as const;

export const PAYMENT_PLANS_STEP_TO_WORKFLOW: StepToWorkflowMap<typeof PAYMENT_PLANS_WORKFLOW_STEPS> =
  buildStepToWorkflowMap(PAYMENT_PLANS_WORKFLOW_STEPS);

export const PAYMENT_PLANS_STEP_TO_EVENT_TYPE: WorkflowEventEventTypeMap<PaymentsPlansStep> = {
  ...generateEventTypeMapDefaults(PaymentsPlansStep, EventType.USER),
  [PaymentsPlansStep.EMPTY_STATE_SHOWN]: EventType.SYSTEM,
  [PaymentsPlansStep.EDIT_IN_CLIENT_PORTAL_FAILED]: EventType.ERROR,
};
